import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PageHeader } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { errMsg, useApi } from "@/lib/api";

export default function MyOrganisation() {
  const api = useApi();
  const [code, setCode] = useState("");
  const [orgs, setOrgs] = useState(null);
  const load = useCallback(() => api.get("/learn/org").then((r) => setOrgs(r.data)).catch((e) => toast.error(errMsg(e))), [api]);
  useEffect(() => { load(); }, [load]);
  const join = async (e) => {
    e.preventDefault();
    try { const r = await api.post("/learn/org/join", { code }); toast.success(`You joined ${r.data.org_name}`); setCode(""); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div>
      <PageHeader eyebrow="Group learning" title="My organisation" testid="org-title" />
      <form onSubmit={join} className="max-w-md space-y-2 rounded-xl border border-slate-200 bg-white p-5" data-testid="org-join-form">
        <Label htmlFor="org-code">Enrolment code from your organisation</Label>
        <div className="flex gap-2"><Input id="org-code" value={code} onChange={(e) => setCode(e.target.value)} required minLength={4} data-testid="org-code-input" /><Button type="submit" className="bg-[#222d99] hover:bg-[#1a237e]" data-testid="org-join-btn">Join</Button></div>
        <p className="text-sm text-slate-600">You keep your existing account and progress.</p>
      </form>
      {orgs?.map((o) => (
        <section key={o.org_name} className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="org-card">
          <h2 className="font-semibold text-slate-900">{o.org_name}</h2>
          {!o.assignments.length && <p className="mt-2 text-sm text-slate-600">No courses assigned yet.</p>}
          <ul className="mt-2 space-y-1 text-sm">{o.assignments.map((a) => <li key={a.course_id}><Link to={`/learn/courses/${a.course_id}`} className="text-[#222d99] underline">{a.course_title}</Link>{a.due_date && ` · due ${a.due_date}`}{a.required && " · required"}</li>)}</ul>
        </section>
      ))}
    </div>
  );
}

export function PaymentResult() {
  const api = useApi();
  const [sp] = useSearchParams();
  const ref = sp.get("reference");
  const [tx, setTx] = useState(null);
  useEffect(() => {
    let n = 0;
    const tick = () => api.get(`/payments/status/${ref}`).then((r) => { setTx(r.data); if (r.data.status === "pending" && n++ < 15) setTimeout(tick, 2000); }).catch((e) => setTx({ status: "error", error: errMsg(e) }));
    if (ref) tick();
  }, [api, ref]);
  const msg = !tx ? "Checking your payment…" : { paid: "Payment confirmed. Your course is unlocked.", pending: "We're waiting for the payment provider to confirm your payment. This page updates automatically.", failed: "The payment didn't go through. You have not been charged for access.", error: tx.error }[tx.status] || "Payment not completed.";
  return (
    <div className="max-w-lg rounded-xl border border-slate-200 bg-white p-6" data-testid="payment-result">
      <h1 className="text-2xl font-bold text-slate-900">Payment</h1>
      <p className="mt-3 text-slate-700" data-testid="payment-result-msg">{msg}</p>
      {tx?.course_id && <Button asChild className="mt-4 bg-[#222d99] hover:bg-[#1a237e]"><Link to={`/learn/courses/${tx.course_id}`} data-testid="payment-course-link">Go to {tx.item_title}</Link></Button>}
    </div>
  );
}
